// 统一处理原则(用户无感):客户端 canvas 编辑工具(resize/crop/rotate/flip/collage/studio/color)
// 遇到浏览器解不了的源(heic/tiff/tga/dds/psd…)时,先经后端 /convert 转成 png(后端 Pillow/rawpy 能解),
// 再进 canvas 编辑——保留完整交互预览。真解不了的(如 ithmb,后端也读不了)→ 后端 4xx → 抛错,工具给"暂不支持"提示。
// 见 [[sharkfoto-freetools-unified-processing]]。(AE 审计 ①)
import { isBrowserDecodable } from "@/settings/free-tools/batch-limits.js";
import { pollJob } from "@/composables/useJobPoll.js";

export function useCanvasDecode() {
  function extOf(file) {
    return (String(file && file.name || "").match(/\.([a-z0-9]+)$/i) || [, ""])[1].toLowerCase();
  }

  // 返回一个 canvas 能解码的 File:浏览器本就能解 → 原样返回;否则后端转 png 返回。
  // 后端同步(200,常见图像如 heic/tiff/dds)直接拿 blob;重负载(202+jobId,如 psd/ai 等 office 类源)
  // 走 /job 轮询 → /result 取产物,与转换器主流程一致。后端也解不了(4xx)→ 抛 Error。仅客户端调用。
  async function decodeForCanvas(file) {
    if (!file) return file;
    const ext = extOf(file);
    if (!ext || isBrowserDecodable(ext)) return file;
    let res;
    try {
      res = await fetch(
        "/api/free-tools/convert?from_format=" + encodeURIComponent(ext) + "&to_format=png",
        { method: "POST", headers: { "Content-Type": "application/octet-stream" }, body: await file.arrayBuffer() }
      );
    } catch (e) {
      throw new Error("decode_network");
    }
    const base = (file.name || "image").replace(/\.[^.]+$/, "");
    // 异步重负载:202 + jobId → 轮询 /job 到 done,再从 /result 取 png blob。
    if (res.status === 202) {
      const { jobId } = await res.json();
      // 解码进 canvas:上限比转换器短(用户在等着开始编辑,别让他干等几分钟)。
      const job = await pollJob(jobId, { maxMs: 120000 });
      if (!job || job.status !== "done") throw new Error("decode_unsupported");
      const rr = await fetch("/api/free-tools/result/" + encodeURIComponent(jobId), { cache: "no-store" });
      if (!rr.ok) throw new Error("decode_unsupported");
      return new File([await rr.blob()], base + ".png", { type: "image/png" });
    }
    if (!res.ok) throw new Error("decode_unsupported");
    return new File([await res.blob()], base + ".png", { type: "image/png" });
  }

  // 源浏览器解不了、后端也解不了时给用户的提示。
  function decodeErrorMsg(file) {
    const ext = extOf(file).toUpperCase() || "This file";
    return `${ext} can't be opened for editing yet. Try converting it to PNG/JPG first.`;
  }

  return { decodeForCanvas, decodeErrorMsg, isBrowserDecodable };
}
